import { useState } from "react";
import { Link, useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Save, ExternalLink, Building2, Users } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  PlatformCustomer,
  PlatformCustomerContact,
  SubscriptionInvoice,
  Workspace,
} from "@shared/schema";

type DetailResponse = {
  customer: PlatformCustomer;
  contacts: PlatformCustomerContact[];
  invoices: SubscriptionInvoice[];
  workspace: Workspace | null;
};

function fmtMoney(cents: number, currency = "usd") {
  const amount = (cents / 100).toFixed(2);
  const sym = currency.toLowerCase() === "usd" ? "$" : "";
  return `${sym}${amount} ${currency.toUpperCase()}`;
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    trial: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    past_due: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    canceled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    churned: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    open: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    uncollectible: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    void: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  };
  return map[status] || "bg-gray-100 text-gray-800";
}

export default function CustomerDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const { toast } = useToast();

  const { data, isLoading, isError } = useQuery<DetailResponse>({
    queryKey: ["/api/platform-customers", id],
    retry: false,
    enabled: !!id,
  });

  const [notes, setNotes] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [dirty, setDirty] = useState(false);

  // initialize once data lands
  if (data && !dirty && (notes === "" && (data.customer.notes || "") !== notes)) {
    // hydrate only the first time
    setTimeout(() => {
      setNotes(data.customer.notes || "");
      setStatus(data.customer.status);
    }, 0);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/platform-customers/${id}`, { notes, status });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Saved" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["/api/platform-customers", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform-customers"] });
    },
    onError: (e: any) => {
      toast({ variant: "destructive", title: "Save failed", description: e?.message || "Try again" });
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading...
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-6 space-y-3" data-testid="state-detail-error">
        <Link href="/customers">
          <Button variant="ghost" size="sm" data-testid="link-back-list"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        </Link>
        <Card><CardContent className="py-8 text-center text-destructive">Customer not found</CardContent></Card>
      </div>
    );
  }

  const { customer, contacts, invoices, workspace } = data;

  return (
    <div className="p-6 space-y-6" data-testid="page-customer-detail">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Link href="/customers">
            <Button variant="ghost" size="sm" data-testid="link-back-list"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          </Link>
          <Building2 className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold" data-testid="text-detail-company">{customer.companyName}</h1>
            <p className="text-sm text-muted-foreground">
              Workspace: {workspace ? <Link href={`/${workspace.slug}`} className="underline">{workspace.slug}</Link> : "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusBadge(customer.status)} data-testid="badge-detail-status">{customer.status}</Badge>
          <Badge variant="outline">{customer.planTier || "—"}</Badge>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardDescription>MRR</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-semibold" data-testid="text-detail-mrr">{fmtMoney(customer.mrrCents || 0)}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Contacts</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-semibold" data-testid="text-detail-contacts-count">{contacts.length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Invoices</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-semibold" data-testid="text-detail-invoices-count">{invoices.length}</div></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Admin notes & status</CardTitle>
          <CardDescription>Free-form notes for the sales team. Status overrides Stripe state until the next webhook event.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => { setStatus(v); setDirty(true); }}>
              <SelectTrigger className="w-48" data-testid="select-detail-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
                <SelectItem value="churned">Churned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="detail-notes">Notes</Label>
            <VoiceToTextTextarea id="detail-notes" data-testid="input-detail-notes" rows={4}
              value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} />
          </div>
          <Button
            data-testid="button-save-detail"
            disabled={!dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Contacts</CardTitle>
          <CardDescription>Mirror of the customer workspace's members. Auto-synced on membership changes.</CardDescription>
        </CardHeader>
        <CardContent>
          {contacts.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground" data-testid="state-no-contacts">
              No active contacts yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Workspace Role</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id} data-testid={`row-contact-${c.id}`}>
                    <TableCell>{c.name || "—"}</TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell><Badge variant="outline">{c.workspaceRole || "—"}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Subscription invoices</CardTitle>
          <CardDescription>Mirrored from Stripe on every invoice.finalized / paid / payment_failed event.</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground" data-testid="state-no-invoices">
              No invoices yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Issued</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`row-invoice-${inv.id}`}>
                    <TableCell>{inv.issuedAt ? new Date(inv.issuedAt as any).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inv.periodStart && inv.periodEnd
                        ? `${new Date(inv.periodStart as any).toLocaleDateString()} → ${new Date(inv.periodEnd as any).toLocaleDateString()}`
                        : "—"}
                    </TableCell>
                    <TableCell>{fmtMoney(inv.amountDueCents || 0, inv.currency)}</TableCell>
                    <TableCell><Badge className={statusBadge(inv.status)}>{inv.status}</Badge></TableCell>
                    <TableCell>
                      {inv.hostedInvoiceUrl ? (
                        <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer"
                           data-testid={`link-invoice-${inv.id}`}
                           className="inline-flex items-center text-sm text-primary hover:underline">
                          View <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
